// Republic Atlas - Search Bar Component

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn, debounce } from '@/utils';
import { useOfficeSearch, useCandidateSearch } from '@/hooks';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface SearchBarProps {
  compact?: boolean;
  placeholder?: string;
}

export function SearchBar({ compact = false, placeholder = 'Search offices, candidates...' }: SearchBarProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce the search query
  const debouncedSetQuery = useCallback(
    debounce((value: string) => setDebouncedQuery(value), 300),
    []
  );

  // Fetch search results
  const { data: offices, isLoading: officesLoading } = useOfficeSearch(debouncedQuery);
  const { data: candidates, isLoading: candidatesLoading } = useCandidateSearch(debouncedQuery);

  const isLoading = officesLoading || candidatesLoading;
  const hasResults = (offices?.length ?? 0) > 0 || (candidates?.length ?? 0) > 0;

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    debouncedSetQuery(value);
    setIsOpen(true);
  };

  // Handle form submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setIsOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleChange}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={cn(
              'w-full bg-surface-elevated border border-white/10 rounded-lg',
              'text-white placeholder-gray-400',
              'focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary',
              'transition-all duration-200',
              compact ? 'pl-10 pr-4 py-2 text-sm' : 'pl-12 pr-4 py-3'
            )}
          />
          <div className={cn(
            'absolute left-0 top-0 h-full flex items-center text-gray-400',
            compact ? 'pl-3' : 'pl-4'
          )}>
            <svg className={cn(compact ? 'w-4 h-4' : 'w-5 h-5')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {isLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <LoadingSpinner size="sm" />
            </div>
          )}
        </div>
      </form>

      {/* Dropdown Results */}
      {isOpen && query.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-surface rounded-xl shadow-lg border border-white/10 overflow-hidden z-50">
          {!hasResults && !isLoading && (
            <div className="px-4 py-8 text-center text-gray-500">
              No results found for "{query}"
            </div>
          )}

          {/* Offices */}
          {offices && offices.length > 0 && (
            <div className="p-2">
              <div className="px-3 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Offices
              </div>
              {offices.slice(0, 3).map((office) => (
                <button
                  key={office.id}
                  onClick={() => {
                    navigate(`/office/${office.id}`);
                    setIsOpen(false);
                    setQuery('');
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-surface-hover rounded-lg transition-colors text-white"
                >
                  <div className="font-medium text-white">{office.name}</div>
                  <div className="text-sm text-gray-400">{office.officeType}</div>
                </button>
              ))}
            </div>
          )}

          {/* Candidates */}
          {candidates && candidates.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <div className="px-3 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Candidates
              </div>
              {candidates.slice(0, 3).map((candidate) => (
                <button
                  key={candidate.id}
                  onClick={() => {
                    navigate(`/candidate/${candidate.id}`);
                    setIsOpen(false);
                    setQuery('');
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-surface-hover rounded-lg transition-colors text-white"
                >
                  <div className="font-medium text-white">{candidate.fullName}</div>
                  <div className="text-sm text-gray-400">{candidate.party}</div>
                </button>
              ))}
            </div>
          )}

          {/* View all results */}
          {hasResults && (
            <div className="p-2 border-t border-gray-100">
              <button
                onClick={handleSubmit}
                className="w-full px-3 py-2 text-sm text-primary hover:bg-primary/20 rounded-lg transition-colors text-center font-medium"
              >
                View all results for "{query}"
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchBar;

